import { useCallback, useEffect, useMemo } from 'react';
import { IconButton, FloatingIconButton } from '@/components';
import { MapLayerManager } from '@/views/map/MapLayerManager/MapLayerManager';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { update as updateMapLayerConfig } from '@/features/mapLayerSlice';
import type { RiskTypeConfig } from './layers/types';
import { LayerType } from './layers/types';
import { useDatasets } from '@/hooks/useDatasets';
import TagManager from 'react-gtm-module';
import {
  getGoogleEventForAnalysis,
  GoogleEvents,
} from '@/features/googleEvents';
import { analysisView } from '@/app/analysis';
import type { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { getAppConfig } from '@/app/config';
import { Layers, X } from 'react-feather';
import { useAppFlags } from '@/hooks/useAppFlags';
import { useHasFuelsData } from '@/hooks/useWildfireMode';
import { TreeCoverControl } from './controls/TreeCoverControl';
import { HazardTreeControl } from './controls/HazardTreeControl';
import { WildfireRiskControl } from './controls/WildfireRiskControl';
import { FuelHazardControl } from './controls/FuelHazardControl';
import { WorktypeAnalysisControl } from './controls/WorktypeAnalysisControl';
import { WorkStatusControl } from './controls/WorkStatusControl';
import { TreeCrownsControl } from './controls/TreeCrownsControl';
import { BaseMapControl } from './controls/BaseMapControl';
import { RedFlagWarningControl } from './controls/RedFlagWarningControl';
import { FallInRiskControl } from './controls/FallInRiskControl';
import { hasLayer } from './controls/utils';
import { useFetchWorkStatusQuery } from '@/features/fieldSlice';

import classes from './LayerManager.module.css';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import Modal from '@/components/Modals/Modal';
import { useClientTerminology } from '@/views/list/NetworkTerms';

interface LayerManagerProps {
  setOpenLayerManager: React.Dispatch<React.SetStateAction<boolean>>;
  openLayerManager: boolean;
}

export const LayerManager = ({
  setOpenLayerManager,
  openLayerManager,
}: LayerManagerProps) => {
  const dispatch = useAppDispatch();
  const { datasets, isFetching } = useDatasets();
  const layerConfig = useAppSelector((s) => s.mapLayer);
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);
  const { flagRedFlagWarning } = useAppFlags();
  const hasFuelsData = useHasFuelsData();
  // Only needed to gate a control inside the opened panel, so defer the fetch
  // until it's open (and share the filter's cache for the same query).
  const { data: workStatusOptions } = useFetchWorkStatusQuery(undefined, {
    skip: !openLayerManager,
  });
  const hasWorkStatusData = (workStatusOptions?.length ?? 0) > 0;
  const isSmallScreen = useSmallScreen();
  const intl = useClientTerminology();

  useEffect(() => {
    if (isFetching || datasets.length === 0) return;
    if (!hasLayer(datasets, 'encroachment')) {
      dispatch(
        updateMapLayerConfig({
          [LayerType.segmentation]: { visible: true },
          [LayerType.encroachment]: { visible: false },
        })
      );
    }
  }, [datasets, isFetching, dispatch]);

  const manageLayers = () => {
    setOpenLayerManager((v: boolean) => {
      const layerToggleOn = getGoogleEventForAnalysis(
        selectedAnalysis,
        GoogleEvents.layerMenuOn
      );

      TagManager.dataLayer({
        dataLayer: {
          event: !v && layerToggleOn,
        },
      });
      return !v;
    });
  };

  const handleLayerManagerClose = useCallback(() => {
    setOpenLayerManager(false);
    const layerToggleOff = getGoogleEventForAnalysis(
      selectedAnalysis,
      GoogleEvents.layerMenuOff
    );

    return TagManager.dataLayer({
      dataLayer: {
        event: layerToggleOff,
      },
    });
  }, [selectedAnalysis, setOpenLayerManager]);

  const analysisMapLayers =
    analysisView[selectedAnalysis as GroupAnalysisType].mapLayers;

  const controls = useMemo(() => {
    return (
      <MapLayerManager>
        {analysisMapLayers.includes(LayerType.critical_trees) && (
          <TreeCrownsControl />
        )}
        {analysisMapLayers.includes(LayerType.segmentation) && (
          <TreeCoverControl />
        )}
        {analysisMapLayers.includes(LayerType.trees) &&
          !(layerConfig[LayerType.trees] as RiskTypeConfig)
            .isHazardTreesOnly && <FallInRiskControl />}
        {hasLayer(datasets, LayerType.trees) &&
          (layerConfig[LayerType.trees] as RiskTypeConfig)
            .isHazardTreesOnly && <HazardTreeControl />}
        {hasLayer(datasets, LayerType.wildfire_risk) &&
          analysisMapLayers.includes(LayerType.wildfire_risk) && (
            <WildfireRiskControl />
          )}
        {hasLayer(datasets, LayerType.fuel_hazard) &&
          analysisMapLayers.includes(LayerType.fuel_hazard) &&
          hasFuelsData && <FuelHazardControl />}
        {hasLayer(datasets, LayerType.wildfire_risk) &&
          analysisMapLayers.includes(LayerType.wildfire_risk) &&
          flagRedFlagWarning && <RedFlagWarningControl />}
        {hasLayer(datasets, LayerType.worktype_analysis) &&
          analysisMapLayers.includes(LayerType.worktype_analysis) &&
          getAppConfig().enableWorktypeAnalysis && <WorktypeAnalysisControl />}
        {hasWorkStatusData && <WorkStatusControl />}
        {hasLayer(datasets, LayerType.base) && <BaseMapControl />}
      </MapLayerManager>
    );
  }, [
    analysisMapLayers,
    datasets,
    flagRedFlagWarning,
    hasFuelsData,
    hasWorkStatusData,
    layerConfig,
  ]);

  return openLayerManager ? (
    isSmallScreen ? (
      <Modal
        opened={openLayerManager}
        onClose={handleLayerManagerClose}
        fullScreen
        title={intl.formatMessage({ id: 'map.layerManager.title' })}
        data-testid="layer-manager"
      >
        {controls}
      </Modal>
    ) : (
      <div className={classes.wrapper} data-testid="layer-manager">
        <header className={classes.header} data-testid="layer-manager-header">
          <div>{intl.formatMessage({ id: 'map.layerManager.title' })}</div>
          <IconButton
            Component={X}
            iconSize={24}
            data-testid="layer-manager-close-button"
            onClick={handleLayerManagerClose}
          />
        </header>
        {controls}
      </div>
    )
  ) : (
    <div id="layer-button" data-testid="layer-button">
      <FloatingIconButton Component={Layers} onClick={manageLayers} />
    </div>
  );
};
